/**
 * lib/letters.ts — «Исполнители» by letters (task 69).
 *
 * The section was biggest-first since task 05 (`apps/api/src/db/queries/catalogue.ts`
 * «Biggest first»): the server still answers in that order, and search still opens a name. The
 * bill of 2026-10-25 draws the section by letters with a strip A–Z at its right edge, and a strip
 * of letters is only a way to a name in an alphabet; so the app sorts, here, and the server does
 * not change.
 *
 * A letter is the first character of the artist's KEY — the server's normalised name
 * (lower case, no punctuation) — so «'Ras Kass» and «ras kass» are under R alike. Latin letters
 * first, then Cyrillic, then «#» for a name that begins with a digit or anything else; inside a
 * letter, by key. Pure, so a test reads it without a screen.
 */

/** The letter of a name that begins with neither a Latin nor a Cyrillic letter. */
export const OTHER_LETTER = '#';

/** Latin 0, Cyrillic 1, everything else 2 — the order the groups and the strip follow. */
function alphabetOf(letter: string): number {
  if (/^[A-Z]$/u.test(letter)) return 0;
  if (/^[А-ЯЁ]$/u.test(letter)) return 1;
  return 2;
}

/** The letter a key is filed under: its first character in upper case, or «#». */
export function letterOf(key: string): string {
  const first = key.trim().charAt(0).toUpperCase();
  return alphabetOf(first) < 2 ? first : OTHER_LETTER;
}

/** Ё stands after Е in the Russian alphabet, not after Я where its code point puts it. */
function rank(letter: string): number {
  if (letter === 'Ё') return 'Е'.charCodeAt(0) + 0.5;
  return letter.charCodeAt(0);
}

export interface LetterGroup<T> {
  readonly letter: string;
  readonly items: readonly T[];
}

/** `items` by letter of `keyOf(item)`: Latin, Cyrillic, «#»; each letter sorted by key. */
export function groupByLetter<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
): LetterGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const letter = letterOf(keyOf(item));
    const group = groups.get(letter);
    if (group === undefined) groups.set(letter, [item]);
    else group.push(item);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => alphabetOf(a) - alphabetOf(b) || rank(a) - rank(b))
    .map(([letter, group]) => ({
      letter,
      items: [...group].sort((a, b) => {
        const ka = keyOf(a);
        const kb = keyOf(b);
        return ka < kb ? -1 : ka > kb ? 1 : 0;
      }),
    }));
}
